import React, { useEffect, useState, useRef } from 'react';
import { parseAdvancedSearchParamsFromString, serializeAdvancedSearchParams } from '../utils/utils';
import ButtonScogoClosedOutlined from './Buttons/ButtonScogoClosedOutlined';
import ButtonScogoPrimary from './Buttons/ButtonScogoPrimary';
import { textCss } from './Css';
import IconToolTip from './IconToolTip';
import Popover from './Popover';
import { useSearchParams } from 'react-router-dom';

const SearchBox = ({
    searchTerm,
    onChange,
    onCancel,
    placeholder,
    allowAdvancedSearch,
    onAdvancedSearchApply = () => { },
    advancedSearchFields = [],
    inputWidth = 'w-full md:w-96',
    inputWidthOnFocus = 'w-full md:w-608',
    showClosedIcon = true,
    onKeyDown = () => { },
    isExpandingEnabled = true,
    autoFocus = true,
    inputClass = '',
    className = ''
}) => {
    const [advancedSearchParams, setAdvancedSearchParams] = useState();
    const [prevAdvancedSearchParams, setPrevAdvancedSearchParams] = useState();
    const [isFocus, setIsFocus] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const iconContainerRef = useRef();
    const popOverRef = useRef();
    const inputRef = useRef(null);
    const isActivelyTyping = useRef(false);
    const blurTimeoutRef = useRef(null);

    useEffect(() => {
        if (typeof searchTerm === 'string' && parseAdvancedSearchParamsFromString(searchTerm)) {
            setAdvancedSearchParams(parseAdvancedSearchParamsFromString(searchTerm));
            setPrevAdvancedSearchParams(parseAdvancedSearchParamsFromString(searchTerm));
        } else {
            setAdvancedSearchParams();
        }
    }, [searchTerm]);

    useEffect(() => {
        // Only restore focus if:
        // 1. User is actively typing
        // 2. Input ref exists
        // 3. Input is not currently focused (lost due to re-render)
        // 4. Input is visible and enabled
        if (
            isActivelyTyping.current &&
            inputRef.current &&
            document.activeElement !== inputRef.current &&
            !inputRef.current.disabled &&
            inputRef.current.offsetParent !== null
        ) {
            const cursorPosition = inputRef.current.selectionStart;
            inputRef.current.focus();
            if (cursorPosition !== null) {
                inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
            }
        }
    });

    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    const isAdvancedSearchActive = advancedSearchParams && Object.keys(advancedSearchParams).length > 0;
    const advancedSearchIcon = (
        <IconToolTip placement={'bottom'} title={`Advanced Search`}>
            <span className={`material-icons hover:text-scogoorange cursor-pointer text-font16`} onClick={() => setIsFocus(true)}>
                tune
            </span>
        </IconToolTip>
    );

    const handleAdvancedSearch = () => {
        Object.entries(advancedSearchParams)
            .map(([key, value]) => {
                setSearchParams(params => {
                    params.set(key, value);
                    return params;
                });
            });
        popOverRef.current.close();
    };

    const advancedSearch = (
        <Popover trigger={advancedSearchIcon} ref={popOverRef} containerRef={iconContainerRef} onClose={() => setIsFocus(false)}>
            <div className={'w-full md:w-608 max-w-full'}>
                {advancedSearchFields.map((field) => (
                    <div className={`px-4 py-3 `} key={field.label}>
                        <label className='block border-b-2 border-scogoddd'>
                            {<p className={`${textCss?.inputLabel}`}>{field.label}</p>}
                            <input
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleAdvancedSearch();
                                    }
                                }}
                                style={{ height: '3.1rem' }}
                                type='text'
                                className={`${textCss?.inputText} block w-full border-none focus:ring-0`}
                                value={advancedSearchParams?.[field.key]}
                                onChange={(e) => setAdvancedSearchParams((prev) => ({ ...prev, [field.key]: e.target.value.trimStart() }))}
                            />
                        </label>
                    </div>
                ))}

                <ButtonScogoClosedOutlined
                    buttonClass='float-right my-5 mr-3'
                    textOrComponent={'Cancel'}
                    onClick={() => {
                        popOverRef.current.close();
                        setAdvancedSearchParams(prevAdvancedSearchParams);
                    }}
                />
                <ButtonScogoPrimary
                    buttonClass='float-right my-5 mx-3'
                    textOrComponent='Search'
                    onClick={() => {
                        handleAdvancedSearch();
                    }}
                />
            </div>
        </Popover>
    );
    const isSearchInputEmpty = !searchTerm || searchTerm.length === 0;

    return (
        <div className={`text-gray-400 focus-within:text-gray-600 relative flex items-center ${className}`}>
            <div className="absolute left-0 flex items-center justify-center ml-2 md:ml-4">
                <img className={'h-5 w-5'} src={process.env.PUBLIC_URL + '/img/search.png'} alt='Search' />
            </div>
            <input
                ref={inputRef}
                value={searchTerm}
                onChange={(e) => {
                    isActivelyTyping.current = true;
                    if (onChange) onChange(e);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        isActivelyTyping.current = true;
                        if (inputRef.current) {
                            setTimeout(() => {
                                inputRef.current.focus();
                            }, 0);
                        }
                    }
                    if (onKeyDown) onKeyDown(e);
                }}
                placeholder={placeholder}
                autoFocus={autoFocus}
                className={`text-xl md:text-scogo15 rounded-md py-3 pl-10 md:pl-12 focus:outline-none w-full border border-scogoccc focus:border-scogoorange focus:ring-0 text-base items-center ${(isFocus || !isSearchInputEmpty) && isExpandingEnabled ? inputWidthOnFocus : inputWidth
                    } transition-all duration-300 ${isAdvancedSearchActive && 'pr-12 md:pr-16'} ${inputClass}`}
                onFocus={(e) => {
                    isActivelyTyping.current = true;
                    setIsFocus(true);
                    if (blurTimeoutRef.current) {
                        clearTimeout(blurTimeoutRef.current);
                        blurTimeoutRef.current = null;
                    }
                }}
                onBlur={(e) => {
                    if (blurTimeoutRef.current) {
                        clearTimeout(blurTimeoutRef.current);
                    }
                    blurTimeoutRef.current = setTimeout(() => {
                        isActivelyTyping.current = false;
                        setIsFocus(false);
                        blurTimeoutRef.current = null;
                    }, 100);
                }}
            />
            <div className='absolute flex items-center right-0 h-full pr-2 md:pr-3' ref={iconContainerRef}>
                {allowAdvancedSearch && advancedSearch}
                {(!isSearchInputEmpty && showClosedIcon) ? (
                    <IconToolTip placement={'bottom'} title={`Clear`}>
                        <span
                            className='material-icons hover:text-scogoorange cursor-pointer text-font16 ml-1'
                            onClick={() => {
                                isActivelyTyping.current = false;
                                onCancel();
                                setAdvancedSearchParams();
                                setPrevAdvancedSearchParams();
                            }}
                        >
                            close
                        </span>
                    </IconToolTip>
                ) : null}
            </div>
        </div>
    );
};

export default SearchBox;
